/**
 * Research Storage Service
 * Handles persistence of research sessions to Supabase
 */

import { supabase } from '@services/supabase';
import {
  ResearchSession,
  ResearchServiceResponse,
  ResearchStatus,
  ResearchSource,
  ResearchQuestion,
  ResearchMetadata,
} from '@/types/research.types';

class ResearchStorageService {
  private readonly tableName = 'research_sessions';

  /**
   * Create a new research session
   */
  async createSession(
    userId: string,
    topic: string
  ): Promise<ResearchServiceResponse<ResearchSession>> {
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expire in 7 days

      const { data, error } = await supabase
        .from(this.tableName)
        .insert({
          user_id: userId,
          topic,
          status: 'in_progress' as ResearchStatus,
          created_at: new Date().toISOString(),
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error('[ResearchStorage] Create session error:', error);
        return {
          data: null,
          error: new Error(error.message),
        };
      }

      return {
        data: this.mapToResearchSession(data),
        error: null,
      };
    } catch (error) {
      console.error('[ResearchStorage] Create session error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * Update research session with results
   */
  async updateSession(
    sessionId: string,
    updates: {
      status?: ResearchStatus;
      questions?: ResearchQuestion[];
      sources?: ResearchSource[];
      finalReport?: string;
      metadata?: ResearchMetadata;
    }
  ): Promise<ResearchServiceResponse<ResearchSession>> {
    try {
      // Map fields to DB column names
      const dbUpdates: Record<string, any> = {};
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.questions !== undefined) dbUpdates.questions = updates.questions;
      if (updates.sources !== undefined) dbUpdates.sources = updates.sources;
      if (updates.finalReport !== undefined) dbUpdates.final_report = updates.finalReport;
      if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;

      const { data, error } = await supabase
        .from(this.tableName)
        .update(dbUpdates)
        .eq('id', sessionId)
        .select()
        .single();

      if (error) {
        console.error('[ResearchStorage] Update session error:', error);
        return {
          data: null,
          error: new Error(error.message),
        };
      }

      return {
        data: this.mapToResearchSession(data),
        error: null,
      };
    } catch (error) {
      console.error('[ResearchStorage] Update session error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * Get a specific research session
   */
  async getSession(sessionId: string): Promise<ResearchServiceResponse<ResearchSession>> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) {
        console.error('[ResearchStorage] Get session error:', error);
        return {
          data: null,
          error: new Error(error.message),
        };
      }

      return {
        data: this.mapToResearchSession(data),
        error: null,
      };
    } catch (error) {
      console.error('[ResearchStorage] Get session error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * Get all research sessions for a user
   * Returns most recent first
   */
  async getUserSessions(
    userId: string,
    limit: number = 20
  ): Promise<ResearchServiceResponse<ResearchSession[]>> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('[ResearchStorage] Get user sessions error:', error);
        return {
          data: null,
          error: new Error(error.message),
        };
      }

      return {
        data: data.map(this.mapToResearchSession),
        error: null,
      };
    } catch (error) {
      console.error('[ResearchStorage] Get user sessions error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * Delete a research session
   */
  async deleteSession(sessionId: string): Promise<ResearchServiceResponse<void>> {
    try {
      const { error } = await supabase.from(this.tableName).delete().eq('id', sessionId);

      if (error) {
        console.error('[ResearchStorage] Delete session error:', error);
        return {
          data: null,
          error: new Error(error.message),
        };
      }

      return {
        data: undefined as void,
        error: null,
      };
    } catch (error) {
      console.error('[ResearchStorage] Delete session error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * Delete expired sessions (called periodically)
   * This helps clean up old research data
   */
  async deleteExpiredSessions(): Promise<ResearchServiceResponse<number>> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .delete()
        .lt('expires_at', new Date().toISOString())
        .select('id');

      if (error) {
        console.error('[ResearchStorage] Delete expired sessions error:', error);
        return {
          data: null,
          error: new Error(error.message),
        };
      }

      return {
        data: data?.length || 0,
        error: null,
      };
    } catch (error) {
      console.error('[ResearchStorage] Delete expired sessions error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  }

  /**
   * Map Supabase row to ResearchSession type
   */
  private mapToResearchSession(data: any): ResearchSession {
    return {
      id: data.id,
      userId: data.user_id,
      topic: data.topic,
      status: data.status,
      questions: data.questions,
      sources: data.sources,
      finalReport: data.final_report,
      metadata: data.metadata,
      createdAt: data.created_at,
      expiresAt: data.expires_at,
    };
  }
}

// Export singleton instance
export const researchStorageService = new ResearchStorageService();
